// Please replace, if IE11-support isn't required any longer.
//import {useFormContext} from 'react-hook-form';
import {useFormContext} from 'react-hook-form/dist/react-hook-form.ie11';

import * as React from 'react';
import {without} from 'lodash';

import {DataSource} from '../Definitions';
import {Block, FormField, Note} from '../LayoutComponents';

type Trip = {
	id?: string;
	name: string;
};

type TripSelectProps = {
	withTrips: () => DataSource;
	getLabel: (key: string) => string;
    onChangeTripSelect?: (id: number, data: any) => void | null;
}
export default ({withTrips, getLabel, onChangeTripSelect}: TripSelectProps) => {
	const {data} = withTrips();

	return React.createElement(
		TripSelect,
		{
			data,
			getLabel,
            onChangeTripSelect
		}
	);
}

const TripSelect = ({data, getLabel, onChangeTripSelect}: any) => {
	const {register, unregister, getValues, setValue, watch, formState: {isSubmitted}} = useFormContext();
	const CUSTOM_VALUE = getLabel('quoteRequest.groupedTrips.trips.other');
	const destinations = getValues()['quoteRequest.groupedTrips'] || [];
	const destinationOptions = Object.keys(data || {})
		.map(value => ({value}))
		.sort((a, b) => a.value.localeCompare(b.value));

	const hasReachedMaximumOfThreeSelectedTrips = destinations.reduce((memo:number, {trips}: any) => memo + trips.length, 0) >= 3;

	const getTripOptions = (destination: string) => (data[destination] || [])
		.sort((a: Trip, b: Trip) => a.name.localeCompare(b.name))
		.map((trip: Trip) =>
			({
				value: [
					trip.name,
					Boolean(trip.id) && `(ReiseID: ${trip.id})`
				]
				.filter(Boolean)
				.join(' '),
				isDisabled: hasReachedMaximumOfThreeSelectedTrips
			})
		)
		.concat(
			({
				value: CUSTOM_VALUE,
				isDisabled: hasReachedMaximumOfThreeSelectedTrips
			})
		);

	React.useEffect(() => {
		register({
			name: 'quoteRequest.groupedTrips'
		}, {
			validate: (value: any) =>
				Array.isArray(value)
				&& Boolean(value.length)
				&& (
					value.reduce((memo:number, {trips}: any) => memo + trips.length, 0) >= 3
					|| value.every(({trips, description}) =>
						trips.length
						&& (
							!trips.includes(CUSTOM_VALUE)
							|| description.length
						)
					)
				)
		});

		return () => unregister('quoteRequest.groupedTrips');
	}, [])

    React.useEffect(() => {
        if (data && onChangeTripSelect) {
            const tmp = getValues()['quoteRequest.groupedTrips'] || [];
            tmp?.forEach((destination: any) => {
                if (destination.trips) {
                    destination.trips?.forEach((trip: string) => {
                        // find tripId
                        const tripId= /\d+/.exec(trip);
                        if (tripId) {
                            onChangeTripSelect(Number(tripId[0]), data);
                        }
                    })
                }
            });
        }
    }, [data]);

	const onSelectDestination = (name: string|number) => {
        const index = destinations.findIndex((group: any) => group.name === name);

        const tmp = index >= 0
            ? destinations.filter((v: any, i: number) => index !== i)
            : [...destinations, {name, trips: [], description: ''}]

		setValue('quoteRequest.groupedTrips', tmp);

        if (onChangeTripSelect && tmp.length === 0) {
            onChangeTripSelect(null, data)
        }
	};


	const onSelectTrip = (name: string) => (trip: string) => {
        if (onChangeTripSelect) {
            // find tripId
            const tripId= /\d+/.exec(trip);
            if (tripId) {
                onChangeTripSelect(Number(tripId[0]), data);
            }
        }

        const index = destinations.findIndex((group: any) => group.name === name);

		if (index < 0) {
			return;
		}

		const trips = destinations[index].trips;
		const tripIndex = trips.indexOf(trip);
		const updatedTrips = tripIndex >= 0
		? without(trips, trip)
		: [...trips, trip];

		setValue('quoteRequest.groupedTrips', destinations.map((destination: any, i: number) => index === i
		? {
			...destination,
			trips: updatedTrips
		}
		: destination
		));
	};

	watch('quoteRequest.groupedTrips');

	return (
		<>
			<Block half>
				<FormField
					name='quoteRequest.groupedTrips'
					type="ControlledMultiSelectbox"
					label={getLabel('quoteRequest.groupedTrips.name')}
					additionalLabel={getLabel('hint.multiselect')}
					options={destinationOptions}
					hasLoader
					onSelect={onSelectDestination}
					selectedValues={destinations.map(({name}: any) => name)}
					required
					isInvalid={destinations.length === 0 && isSubmitted}
				/>
			</Block>

			{
				(data && Object.keys(data).length) && (
					<Block half>
						{
							hasReachedMaximumOfThreeSelectedTrips
								? <Note isInputLike text={getLabel('quoteRequest.groupedTrips.maximumReached')}/>
								: null
						}
						{
							destinations.map(({name: destination, trips}: any, index: number) => {
								return (
									<React.Fragment key={destination}>
										<FormField
											name='quoteRequest.groupedTrips'
											type="ControlledMultiSelectbox"
											label={`${destination}: ${getLabel('quoteRequest.groupedTrips.trips')}`}
											additionalLabel={getLabel('hint.multiselect')}
											options={getTripOptions(destination)}
											onSelect={onSelectTrip(destination)}
											selectedValues={trips}
											required={!hasReachedMaximumOfThreeSelectedTrips}
											hasValue={trips.length > 0}
											isInvalid={trips.length === 0 && isSubmitted && !hasReachedMaximumOfThreeSelectedTrips}
										/>
										{
											Boolean(trips.includes(CUSTOM_VALUE)) && (
												<FormField
													type="TextInput"
													name={`quoteRequest.groupedTrips[${index}].description`}
													label={getLabel('quoteRequest.groupedTrips.description')}
													required
													key={destination + 'description'}
												/>
											)
										}
									</React.Fragment>
								)
							})
						}
					</Block>
				)
			}

		</>
	);
};
